import {
  OrderLabourInfo,
  OrderResponse,
  VehicleShort,
} from 'src/api/cart/interfaces'
import { LaborItem } from 'src/store/models/LaborModel'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { store } from 'store'
import { SellerCartVehicle } from '../store/ISellerCart'
import { setProcessingOrder } from '../store/uiStore'
import { getInvalidVehicles, makeOrderRequest, trackPurchase } from './Utils'
import { OrderFormValidationException } from '../exceptions/OrderFormValidationException'
import OrderServiceProvider from 'src/services/OrderServiceProvider'
import { IS_INTEGRATED_VERSION } from 'src/config/ConfigManager'
import { EmbeddedCommunicationsManager } from 'src/api/embedded/EmbeddedCommunicationsManager'
import { cartSlice } from '../store/cartStore'
import { clearSpecsAndLaborIfNoParts } from '../store/helpers'

const findVehicleIndexByOrderResponse = (
  vehicleResponse: VehicleShort
): number => {
  return (store.getState().cart.cartData.vehicles || []).findIndex((item) => {
    const baseMatch =
      item?.vehicle?.year?.value === vehicleResponse?.year &&
      item?.vehicle?.make?.value === vehicleResponse?.make &&
      item?.vehicle?.model?.value === vehicleResponse?.model &&
      item?.vehicle?.engine?.value === vehicleResponse?.engine

    const subModelMatch =
      StoreInstances?.userStore?.isCclEnabled && vehicleResponse?.subModel
        ? item?.vehicle?.subModel?.value === vehicleResponse?.subModel
        : true

    return baseMatch && subModelMatch
  })
}

const getLaborFromOrderDetailLocations = (
  orderDetail: OrderResponse['orderDetails'][number]
): LaborItem[] => {
  const laborItems: LaborItem[] = []

  orderDetail.locations?.forEach((location) => {
    const labors = (location as { labors?: OrderLabourInfo[] }).labors
    labors?.forEach((labor) => {
      laborItems.push({
        laborId: labor.laborId,
        laborDescription: labor.description,
        details: labor.details,
        hours: labor.hours,
        laborRate: String(labor.rate),
        rate: labor.rate,
        warrantyHrs: labor.warrantyHours,
        skillLevel: labor.skillLevel,
        miscellaneousText: labor.notes,
        bold: '',
        groupId: '',
        unansweredSpecific: '',
      })
    })
  })

  return laborItems
}

const addSpecificationAndLaborToOrderResponse = (
  currentOrder: OrderResponse
): OrderResponse => {
  return {
    ...currentOrder,
    orderDetails: currentOrder.orderDetails.map((orderDetail, index) => {
      const vehicleIndex = findVehicleIndexByOrderResponse(orderDetail.vehicle)
      const cartVehicle =
        vehicleIndex >= 0
          ? store.getState().cart.cartData.vehicles[vehicleIndex]
          : store.getState().cart.cartData.vehicles[index]

      const laborFromCart =
        cartVehicle?.laborResults?.length > 0 ? cartVehicle.laborResults : []
      const laborFromLocations = getLaborFromOrderDetailLocations(orderDetail)
      const laborData =
        laborFromCart.length > 0
          ? laborFromCart
          : laborFromLocations.length > 0
            ? laborFromLocations
            : (orderDetail.laborData ?? [])

      return {
        ...orderDetail,
        specifications:
          cartVehicle?.specifications?.length > 0
            ? cartVehicle.specifications
            : (orderDetail.specifications ?? []),
        laborData,
      }
    }),
  }
}

export const placeOrder = async (
  vehicles: SellerCartVehicle[]
): Promise<OrderResponse | undefined> => {
  // Start global processing
  store.dispatch(setProcessingOrder(true))
  try {
    store.dispatch(cartSlice.actions.setDisplayCartFormErrors(true))
    const sellerOrderRequest = makeOrderRequest(vehicles)
    const invalidVehicles = getInvalidVehicles(vehicles)
    if (invalidVehicles.length) {
      throw new OrderFormValidationException('orderFormIsIncomplete')
    }

    const response =
      await OrderServiceProvider.makeMultiSellerOrder(sellerOrderRequest)

    let responseWithSpecsAndLabor

    if (response) {
      responseWithSpecsAndLabor =
        addSpecificationAndLaborToOrderResponse(response)

      // Track purchase event
      const { countryCode } = StoreInstances.userStore.country || {}
      if (countryCode) {
        trackPurchase(responseWithSpecsAndLabor, countryCode)
      }

      if (IS_INTEGRATED_VERSION) {
        EmbeddedCommunicationsManager.transferOrderResponse(
          store.getState().cart.cartData,
          responseWithSpecsAndLabor
        )
      }

      // Clean up specs and labor from vehicles without parts after successful order
      // Use setTimeout to ensure cart cleanup happens first
      setTimeout(() => {
        store.dispatch(cartSlice.actions.clearSpecsAndLaborAfterOrder())
      }, 500)
    }
    clearSpecsAndLaborIfNoParts()
    return responseWithSpecsAndLabor
  } finally {
    // Always reset processingOrder even if there’s an error
    store.dispatch(setProcessingOrder(false))
  }
}

export const orderAllCarts = async (): Promise<OrderResponse | undefined> => {
  const cartsWithProducts = store
    .getState()
    .cart.cartData.vehicles.filter((v) =>
      v.sellers?.filter((s) => s.locations?.filter((l) => l.parts))
    )

  const orderResponse = await placeOrder(cartsWithProducts)

  return orderResponse
}
